import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { Eye, Heart, ChatCircle, ArrowRight, MagnifyingGlass, Tag, Star, CaretUpDown, Check, X, Funnel } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export default function Articles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const [categories, setCategories] = useState([]);
  const [trendingTags, setTrendingTags] = useState([]);
  const [availableSubCats, setAvailableSubCats] = useState([]);
  const [categoryId, setCategoryId] = useState(searchParams.get("category_id") || "all");
  const [subCategory, setSubCategory] = useState(searchParams.get("sub_category") || "");
  const [tag, setTag] = useState(searchParams.get("tag") || "");
  const [catOpen, setCatOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cats, tags] = await Promise.all([
          api.get("/categories"),
          api.get("/trending-tags")
        ]);
        setCategories(cats.data);
        setTrendingTags(tags.data);
      } catch (e) { }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/discover/sub-categories", { params: { category_id: categoryId } });
        setAvailableSubCats(data);
      } catch (e) { }
    })();
  }, [categoryId]);

  const fetchArticles = async (p = 1, query = "", filters = {}, append = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/articles`, {
        params: {
          page: p,
          limit: 12,
          q: query.trim() || undefined,
          category_id: filters.categoryId !== "all" ? filters.categoryId : undefined,
          sub_category: filters.subCategory.trim() || undefined,
          tag: filters.tag.trim() || undefined
        }
      });
      if (append) {
        setItems(prev => [...(prev || []), ...data.articles]);
      } else {
        setItems(data.articles || []);
      }
      setMeta(data.meta);
    } catch (e) {
      setErr("Couldn't load the articles.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchArticles(1, search, { categoryId, subCategory, tag }, false);

      const params = {};
      if (search) params.q = search;
      if (categoryId !== "all") params.category_id = categoryId;
      if (subCategory) params.sub_category = subCategory;
      if (tag) params.tag = tag;
      setSearchParams(params);
    }, 400);

    return () => clearTimeout(handler);
  }, [search, categoryId, subCategory, tag]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchArticles(next, search, { categoryId, subCategory, tag }, true);
  };

  const resetFilters = () => {
    setSearch("");
    setCategoryId("all");
    setSubCategory("");
    setTag("");
  };

  if (items === null && page === 1) return <div className="max-w-5xl mx-auto px-6 py-20 text-muted-foreground">Loading...</div>;


  return (
    <div className="max-w-7xl mx-auto px-6 py-10 lg:py-16">
      <div className="mb-10 lg:mb-12">
        <div className="eyebrow text-muted-foreground mb-3">The collection</div>
        <h1 className="font-serif text-4xl sm:text-6xl font-black tracking-tight">Articles.</h1>
        <p className="mt-4 text-muted-foreground max-w-2xl text-base lg:text-lg">
          Explore a curated library of essays, technical deep-dives, and creative stories from across the Inkspot collective.
        </p>
      </div>

      {/* Mobile Filter Toggle */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="lg:hidden w-full mb-8 flex items-center justify-between border border-border p-4 bg-card"
      >
        <span className="eyebrow text-xs">Filter & Search</span>
        <Funnel size={18} weight={showFilters ? "fill" : "regular"} />
      </button>

      <div className="grid grid-cols-12 gap-8 lg:gap-12">
        {/* SIDEBAR */}
        <aside className={`col-span-12 lg:col-span-3 space-y-8 ${showFilters ? "block" : "hidden lg:block"}`}>
          <div>
            <Label className="eyebrow text-muted-foreground mb-3 block">Search</Label>
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Title, excerpt..."
                className="rounded-none pl-10 h-11 bg-card border-border"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label className="eyebrow text-muted-foreground mb-3 block">Category</Label>
            <Popover open={catOpen} onOpenChange={setCatOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between rounded-none font-normal border-border bg-card h-11"
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <Tag size={14} className="shrink-0" />
                    <span className="truncate">
                      {categoryId !== "all"
                        ? categories.find((c) => c.id === categoryId)?.name
                        : "All Categories"}
                    </span>
                  </div>
                  <CaretUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-0 rounded-none shadow-xl border-border" align="start">
                <Command>
                  <CommandInput placeholder="Search..." />
                  <CommandList>
                    <CommandEmpty>No results.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setCategoryId("all");
                          setCatOpen(false);
                        }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", categoryId === "all" ? "opacity-100" : "opacity-0")} />
                        All Categories
                      </CommandItem>
                      {categories.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={c.name}
                          onSelect={() => {
                            setCategoryId(c.id);
                            setCatOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", categoryId === c.id ? "opacity-100" : "opacity-0")} />
                          {c.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label className="eyebrow text-muted-foreground mb-3 block">Sub-category</Label>
            <Popover open={subOpen} onOpenChange={setSubOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between rounded-none font-normal border-border bg-card h-11"
                >
                  <span className="truncate">{subCategory || "All sub-topics"}</span>
                  <CaretUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-0 rounded-none shadow-xl border-border" align="start">
                <Command>
                  <CommandInput placeholder="Search..." />
                  <CommandList>
                    <CommandEmpty>No results.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value=""
                        onSelect={() => {
                          setSubCategory("");
                          setSubOpen(false);
                        }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", !subCategory ? "opacity-100" : "opacity-0")} />
                        All sub-topics
                      </CommandItem>
                      {availableSubCats.map((s) => (
                        <CommandItem
                          key={s}
                          value={s}
                          onSelect={() => {
                            setSubCategory(s);
                            setSubOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", subCategory === s ? "opacity-100" : "opacity-0")} />
                          {s}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label className="eyebrow text-muted-foreground mb-3 block">Tags</Label>
            <div className="relative">
              <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Custom tag..."
                className="rounded-none pl-10 h-11 bg-card border-border"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {trendingTags.map((t) => (
                <button
                  key={t}
                  onClick={() => setTag(tag === t ? "" : t)}
                  className={cn(
                    "px-3 py-1 text-[10px] uppercase font-bold tracking-widest border transition-all",
                    tag === t
                      ? "bg-foreground text-background border-foreground"
                      : "bg-transparent text-muted-foreground border-border hover:border-foreground hover:text-foreground"
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {(search || categoryId !== "all" || subCategory || tag) && (
            <Button
              variant="ghost"
              onClick={resetFilters}
              className="w-full rounded-none text-xs h-10 hover:bg-destructive/10 hover:text-destructive gap-2 border border-dashed border-border mt-4"
            >
              <X size={14} /> Clear all filters
            </Button>
          )}

          <div className="pt-8 border-t border-border">
            <div className="text-[10px] uppercase font-mono text-muted-foreground">
              {loading ? "Updating results..." : `${meta?.total || 0} articles found`}
            </div>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <div className="col-span-12 lg:col-span-9">
          {err ? <div className="text-sm text-destructive bg-destructive/10 p-4 border border-destructive/20">{err}</div> : null}

          {items.length === 0 ? (
            <div className="border border-dashed border-border p-20 text-center bg-card">
              <div className="font-serif text-3xl font-black">No articles found</div>
              <p className="mt-4 text-muted-foreground max-w-sm mx-auto">
                We couldn't find any essays matching your current filters. Try broadening your search.
              </p>
              <Button variant="outline" onClick={resetFilters} className="mt-8 rounded-none px-8">Reset everything</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {items.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.3) }}
                >
                  <Link
                    to={`/${a.author.username}/${a.slug}`}
                    className="group block border border-border bg-card lift-card h-full flex flex-col"
                  >
                    <div className="aspect-[16/10] overflow-hidden border-b border-border bg-secondary relative">
                      {a.cover_image ? (
                        <img src={fileUrl(a.cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full grid place-items-center opacity-20"><Funnel size={40} /></div>
                      )}
                      {a.category && (
                        <div className="absolute top-3 left-3 bg-orange-600 text-white text-[10px] font-black uppercase px-3 py-1.5 shadow-lg z-20 tracking-widest">
                          {a.category.name}
                        </div>
                      )}
                      {a.is_featured && (
                        <div className="absolute top-3 right-3 bg-yellow-500 text-black text-[10px] font-black uppercase px-3 py-1.5 flex items-center gap-1 shadow-lg z-10 tracking-widest">
                          <Star size={12} weight="fill" /> Featured
                        </div>
                      )}
                    </div>

                    <div className="p-5 flex-grow flex flex-col">
                      <div className="flex items-center gap-2 mb-3">
                        {a.author.profile_picture ? (
                          <img src={fileUrl(a.author.profile_picture)} alt="" className="h-5 w-5 rounded-full object-cover" />
                        ) : (
                          <div className="h-5 w-5 bg-secondary rounded-full grid place-items-center text-[8px] font-bold">{(a.author.name || "A")[0]}</div>
                        )}
                        <span className="text-[10px] font-bold tracking-tight uppercase">{a.author.name || a.author.username}</span>
                      </div>

                      <div className="eyebrow text-[9px] text-muted-foreground flex flex-wrap items-center gap-1.5 mb-2">
                        <span>{new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                        <span>·</span>
                        <span>{a.reading_time || 1}m read</span>
                      </div>

                      <h2 className="font-serif text-xl font-black leading-tight group-hover:underline underline-offset-4 decoration-1">{a.title}</h2>
                      {a.excerpt ? <p className="text-xs text-muted-foreground mt-3 line-clamp-3 leading-relaxed">{a.excerpt}</p> : null}
                    </div>

                    <div className="px-5 pb-5 mt-auto flex items-center justify-between border-t border-border/50 pt-4">
                      <div className="flex items-center gap-4 text-[10px] text-muted-foreground font-mono">
                        <span className="flex items-center gap-1"><Eye size={12} /> {a.views}</span>
                        <span className="flex items-center gap-1"><Heart size={12} /> {a.likes_count}</span>
                        <span className="flex items-center gap-1"><ChatCircle size={12} /> {a.comments_count}</span>
                      </div>
                      <ArrowRight size={14} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {meta?.has_more && (
            <div className="mt-16 text-center border-t border-border pt-12">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={loading}
                className="rounded-none px-12 h-14 font-serif text-lg font-bold border-2 hover:bg-foreground hover:text-background transition-all"
              >
                {loading ? "Finding more..." : "Show more articles"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
